// Genre catalog v1 — Song Library taxonomy helpers.
//
// Pure functions so the PATCH rules ("only send changed fields", "clearing
// genre clears subgenre") and the list-filter wiring ("subgenreId only valid
// with genreId") are unit-testable without mounting the page.

import type { ListTracksArgs } from "@/features/tracks/tracksApi";
import type { UpdateTrackGenreArgs } from "@/features/tracks/tracksApi";

export interface TrackGenreSelection {
  genreId: string | null;
  subgenreId: string | null;
}

function normalize(value: string | null | undefined): string | null {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Build the PATCH body fields for a Song's Genre/Subgenre edit.
 *
 * - Unchanged fields are NOT sent at all (undefined, key omitted).
 * - Clearing the Genre clears the Subgenre: the form enforces it, and this
 *   builder re-enforces it so the payload can never carry a subgenre without
 *   its genre.
 * - Returns an empty object when nothing changed.
 */
export function buildTrackGenrePatch(
  original: TrackGenreSelection,
  next: TrackGenreSelection,
): Omit<UpdateTrackGenreArgs, "trackId"> {
  const origGenre = normalize(original.genreId);
  const origSubgenre = normalize(original.subgenreId);
  let nextGenre = normalize(next.genreId);
  let nextSubgenre = normalize(next.subgenreId);

  // Clearing genre clears subgenre; a subgenre never travels without a genre.
  if (nextGenre == null) nextSubgenre = null;

  const patch: Omit<UpdateTrackGenreArgs, "trackId"> = {};
  if (nextGenre !== origGenre) patch.genreId = nextGenre;
  if (nextSubgenre !== origSubgenre) patch.subgenreId = nextSubgenre;
  return patch;
}

export function trackGenrePatchIsEmpty(
  patch: Omit<UpdateTrackGenreArgs, "trackId">,
): boolean {
  return !("genreId" in patch) && !("subgenreId" in patch);
}

/**
 * Wire the Song Library Genre/Subgenre filter into the tracks list query
 * args. subgenreId is only valid together with genreId — a dangling
 * subgenre filter is dropped.
 */
export function applyGenreFilterToTrackListArgs(
  base: ListTracksArgs,
  genreFilterId: string | undefined,
  subgenreFilterId: string | undefined,
): ListTracksArgs {
  const genreId = normalize(genreFilterId);
  if (!genreId) return base;
  const subgenreId = normalize(subgenreFilterId);
  return subgenreId ? { ...base, genreId, subgenreId } : { ...base, genreId };
}
